import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { MAP_TILES, DEFAULT_TILE } from '../utils/mapTiles';
import {
  MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMap
} from 'react-leaflet';
import L from 'leaflet';
import { io } from 'socket.io-client';

// ── Leaflet icon fix ───────────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const guardIcon = (moving) => L.divIcon({
  html: `<div style="
    background:${moving ? '#22c55e' : '#4361ee'};
    border:3px solid white;border-radius:50%;
    width:28px;height:28px;
    display:flex;align-items:center;justify-content:center;
    font-size:14px;box-shadow:0 2px 10px rgba(0,0,0,0.4);
    animation:${moving ? 'pulse 1.5s infinite' : 'none'};
  ">👮</div>
  <style>@keyframes pulse{0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,.6)}50%{box-shadow:0 0 0 10px rgba(34,197,94,0)}}</style>`,
  className: '', iconSize: [28, 28], iconAnchor: [14, 14],
});

const cpIcon = (done, active) => L.divIcon({
  html: `<div style="
    background:${done ? '#22c55e' : active ? '#f59e0b' : '#e5e7eb'};
    border:2px solid white;border-radius:50%;
    width:20px;height:20px;
    display:flex;align-items:center;justify-content:center;
    font-size:10px;font-weight:700;color:white;
    box-shadow:0 2px 6px rgba(0,0,0,0.3);
  ">${done ? '✓' : '●'}</div>`,
  className: '', iconSize: [20, 20], iconAnchor: [10, 10],
});

// ── Auto-pan map to guard position ────────────────────────────
function PanToGuard({ pos }) {
  const map = useMap();
  useEffect(() => {
    if (pos) map.panTo(pos, { animate: true, duration: 0.8 });
  }, [pos]);
  return null;
}

// ── Interpolate position between two points ────────────────────
function interpolate(from, to, t) {
  return {
    lat: from.lat + (to.lat - from.lat) * t,
    lng: from.lng + (to.lng - from.lng) * t,
  };
}

// ── Add small GPS jitter (realistic simulation) ────────────────
function jitter(val, amount = 0.00003) {
  return val + (Math.random() - 0.5) * amount;
}

export default function GuardSimulator() {
  const [tile, setTile] = useState(DEFAULT_TILE);
  const [routes, setRoutes]             = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [checkpoints, setCheckpoints]   = useState([]);
  const [activePatrol, setActivePatrol] = useState(null);
  const [scannedIds, setScannedIds]     = useState([]);
  const [selectedCP, setSelectedCP]     = useState(null);
  const [photo, setPhoto]               = useState(null);
  const [notes, setNotes]               = useState('');
  const [submitting, setSubmitting]     = useState(false);
  const [scanResult, setScanResult]     = useState(null);

  // Simulation state
  const [guardPos, setGuardPos]         = useState(null);   // { lat, lng }
  const [trail, setTrail]               = useState([]);      // array of [lat, lng]
  const [simRunning, setSimRunning]       = useState(false);
  const [simSpeed, setSimSpeed]           = useState(2);       // 1=slow, 2=normal, 3=fast
  const [nextCpIdx, setNextCpIdx]         = useState(0);       // which CP to walk to next
  const [simLog, setSimLog]               = useState([]);       // log messages
  const [socketStatus, setSocketStatus]   = useState('connecting');

  const fileRef         = useRef();
  const simInterval     = useRef(null);
  const locationInterval = useRef(null);
  const socketRef       = useRef(null);
  const animFrame       = useRef(0);    // progress 0→1 between CPs
  const fromPos         = useRef(null);
  const toPos           = useRef(null);

  // Connect socket — use polling first so it works on Render free tier
  useEffect(() => {
    const url = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.hostname.includes('pages.dev') ? 'https://guard-patrol-73go.onrender.com' : 'http://localhost:5003');
    socketRef.current = io(url, {
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });
    socketRef.current.on('connect', () => {
      setSocketStatus('connected');
      addLog('🔌 Socket connected to server', 'success');
    });
    socketRef.current.on('disconnect', () => setSocketStatus('disconnected'));
    socketRef.current.on('connect_error', () => setSocketStatus('disconnected'));
    return () => socketRef.current?.disconnect();
  }, []);

  useEffect(() => {
    axios.get('/api/routes').then(r => setRoutes(r.data.filter(x => x.is_active)));
  }, []);

  const addLog = (msg, type = 'info') => {
    const colors = { info: '#4361ee', success: '#22c55e', warn: '#f59e0b', error: '#ef233c' };
    setSimLog(prev => [{ msg, time: new Date().toLocaleTimeString(), color: colors[type] }, ...prev].slice(0, 30));
  };

  // ── Start patrol ─────────────────────────────────────────────
  const startPatrol = async (route) => {
    try {
      // End any existing active patrol for this user first
      const existing = await axios.get('/api/patrols').catch(() => ({ data: [] }));
      const myActive = (existing.data || []).find(p => p.status === 'active');
      if (myActive) {
        await axios.post(`/api/patrols/${myActive.id}/end`).catch(() => {});
        addLog('⏹ Previous patrol auto-ended', 'warn');
      }

      const res = await axios.post('/api/patrols/start', { route_id: route.id });
      const cpRes = await axios.get(`/api/checkpoints/route/${route.id}`);
      const cps = cpRes.data;

      setActivePatrol(res.data);
      setSelectedRoute(route);
      setCheckpoints(cps);
      setScannedIds([]);
      setNextCpIdx(0);
      setSimLog([]);

      // Start guard at first checkpoint
      if (cps.length > 0) {
        const firstWithCoords = cps.find(c => c.latitude != null && c.longitude != null);
        const start = firstWithCoords
          ? { lat: firstWithCoords.latitude, lng: firstWithCoords.longitude }
          : { lat: 23.0225, lng: 72.5714 };
        setGuardPos(start);
        setTrail([[start.lat, start.lng]]);
        fromPos.current = start;
        toPos.current = start;
      }

      toast.success(`Patrol started: ${route.name}`);
      addLog(`🚶 Patrol started on route: ${route.name}`, 'success');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start patrol');
    }
  };

  // ── Emit location to backend — dual path: HTTP + socket ──────
  const emitLocation = useCallback((pos) => {
    if (!activePatrol?.id) return;
    // HTTP path — saved to DB + triggers req.io.emit
    axios.post(`/api/patrols/${activePatrol.id}/location`, {
      latitude: pos.lat, longitude: pos.lng, accuracy_meters: 8,
    }).catch(() => {});
    // Socket direct path — instant, no HTTP overhead
    socketRef.current?.emit('guard:location', {
      guardId:   activePatrol.guard_id || activePatrol.id,
      guardName: 'Guard Simulator',
      patrolId:  activePatrol.id,
      latitude:  pos.lat,
      longitude: pos.lng,
      timestamp: new Date(),
    });
  }, [activePatrol]);

  // ── Simulation tick — move guard smoothly between CPs ────────
  useEffect(() => {
    if (!simRunning || !checkpoints.length || !activePatrol) return;

    const STEP = 0.025 * simSpeed; // progress per tick
    const TICK = 200;              // ms per tick

    simInterval.current = setInterval(() => {
      const cps = checkpoints;
      const idx = nextCpIdx;
      if (idx >= cps.length) return; // all CPs reached

      const target = cps[idx];
      if (target.latitude == null || target.longitude == null) {
        if (!scannedIds.includes(target.id)) {
          setSelectedCP(target);
          addLog(`📸 Photo checkpoint CP${target.sequence_order}: ${target.name} ready for scan`, 'info');
        }
        setNextCpIdx(prev => prev + 1);
        return;
      }
      const dest = { lat: target.latitude, lng: target.longitude };

      if (!fromPos.current) { fromPos.current = dest; }
      toPos.current = dest;

      animFrame.current = Math.min(animFrame.current + STEP, 1);
      const pos = interpolate(fromPos.current, dest, animFrame.current);
      const jPos = { lat: jitter(pos.lat), lng: jitter(pos.lng) };

      setGuardPos(jPos);
      setTrail(prev => [...prev, [jPos.lat, jPos.lng]]);

      // Arrived at checkpoint
      if (animFrame.current >= 1) {
        animFrame.current = 0;
        fromPos.current = dest;
        addLog(`📍 Arrived at CP${target.sequence_order}: ${target.name}`, 'warn');

        // Auto-scan if not already scanned
        if (!scannedIds.includes(target.id)) {
          setSelectedCP(target);
        }
      }
    }, TICK);

    return () => clearInterval(simInterval.current);
  }, [simRunning, nextCpIdx, checkpoints, activePatrol, simSpeed]);

  // ── Location broadcast interval (every 1s) ────────────────────
  useEffect(() => {
    if (!simRunning || !guardPos) return;
    // Emit immediately on position update
    emitLocation(guardPos);
    locationInterval.current = setInterval(() => emitLocation(guardPos), 1000);
    return () => clearInterval(locationInterval.current);
  }, [simRunning, guardPos, emitLocation]);

  // ── Auto-scan checkpoint when arrived ────────────────────────
  const autoScanCP = async (cp) => {
    if (scannedIds.includes(cp.id)) return;
    const isGps = cp.scan_type === 'gps';
    try {
      const formData = new FormData();
      formData.append('patrol_id',     activePatrol.id);
      formData.append('checkpoint_id', cp.id);
      formData.append('latitude',      cp.latitude != null ? (cp.latitude + (Math.random() - 0.5) * 0.00003).toFixed(7) : (guardPos ? guardPos.lat.toFixed(7) : '23.0225000'));
      formData.append('longitude',     cp.longitude != null ? (cp.longitude + (Math.random() - 0.5) * 0.00003).toFixed(7) : (guardPos ? guardPos.lng.toFixed(7) : '72.5714000'));
      formData.append('accuracy_meters', '5');
      formData.append('notes',         'Auto-scanned by simulator');

      const doScan = async (fd) => {
        const res = await axios.post('/api/scans', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data.verification?.status === 'verified') {
          setScannedIds(prev => [...prev, cp.id]);
          addLog(`✅ CP${cp.sequence_order} "${cp.name}" verified!`, 'success');
          setNextCpIdx(prev => prev + 1);
        } else {
          addLog(`❌ CP${cp.sequence_order} scan failed: ${res.data.verification?.message}`, 'error');
          setNextCpIdx(prev => prev + 1);
        }
      };

      if (isGps) {
        // GPS-only: no photo needed
        await doScan(formData);
      } else {
        // Photo type: create a dummy image
        const canvas = document.createElement('canvas');
        canvas.width = 100; canvas.height = 100;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#4361ee'; ctx.fillRect(0, 0, 100, 100);
        ctx.fillStyle = 'white'; ctx.font = '14px sans-serif';
        ctx.fillText(`CP${cp.sequence_order}`, 20, 55);
        canvas.toBlob(async (blob) => {
          formData.append('image', blob, `cp${cp.sequence_order}.png`);
          await doScan(formData);
        }, 'image/png');
      }
    } catch (err) {
      addLog(`⚠️ Scan error: ${err.message}`, 'error');
      setNextCpIdx(prev => prev + 1);
    }
  };

  // Auto-scan when selectedCP changes during simulation
  useEffect(() => {
    if (simRunning && selectedCP && !scannedIds.includes(selectedCP.id)) {
      setTimeout(() => autoScanCP(selectedCP), 800);
    }
  }, [selectedCP, simRunning]);

  // ── Manual scan (when sim not running) ───────────────────────
  const submitManualScan = async () => {
    const isGps = selectedCP?.scan_type === 'gps';
    if (!isGps && !photo) return toast.error('Please select a photo');
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('patrol_id',     activePatrol.id);
      formData.append('checkpoint_id', selectedCP.id);
      // Use exact checkpoint coords with tiny jitter (max 3m off)
      formData.append('latitude',      (selectedCP.latitude  + (Math.random() - 0.5) * 0.00003).toFixed(6));
      formData.append('longitude',     (selectedCP.longitude + (Math.random() - 0.5) * 0.00003).toFixed(6));
      formData.append('accuracy_meters', '5');
      formData.append('notes', notes);
      if (!isGps && photo) formData.append('image', photo);
      const res = await axios.post('/api/scans', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setScanResult(res.data.verification);
      if (res.data.verification?.status === 'verified') {
        setScannedIds(prev => [...prev, selectedCP.id]);
        setNextCpIdx(prev => prev + 1);
        addLog(`✅ CP${selectedCP.sequence_order} manually verified`, 'success');
        toast.success('Checkpoint verified!');
      } else {
        toast.error(res.data.verification?.message || 'Out of range');
      }
    } catch (err) {
      toast.error(err.response?.data?.verification?.message || err.response?.data?.error || 'Scan failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ── End patrol ────────────────────────────────────────────────
  const endPatrol = async () => {
    setSimRunning(false);
    clearInterval(simInterval.current);
    clearInterval(locationInterval.current);
    await axios.post(`/api/patrols/${activePatrol.id}/end`);
    const compliance = checkpoints.length
      ? Math.round((scannedIds.length / checkpoints.length) * 100) : 0;
    toast.success(`Patrol ended — ${compliance}% compliance`);
    addLog(`🏁 Patrol ended. Compliance: ${compliance}%`, 'success');
    setActivePatrol(null); setSelectedRoute(null);
    setCheckpoints([]); setScannedIds([]); setGuardPos(null);
    setTrail([]); setSimLog([]); setNextCpIdx(0);
  };

  const compliance = checkpoints.length
    ? Math.round((scannedIds.length / checkpoints.length) * 100) : 0;

  const gpsCP = checkpoints.find(cp => cp.latitude && cp.longitude);
  const mapCenter = gpsCP
    ? [gpsCP.latitude, gpsCP.longitude]
    : [22.2587, 71.1924];

  // ────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div className="page-title">👮 Guard Patrol Simulator</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="badge badge-info">Simulates the mobile guard app</span>
          {activePatrol && <span className="badge badge-success">🟢 Patrol Active</span>}
          <span style={{
            fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600,
            background: socketStatus === 'connected' ? '#dcfce7' : socketStatus === 'connecting' ? '#fef3c7' : '#fee2e2',
            color: socketStatus === 'connected' ? '#166534' : socketStatus === 'connecting' ? '#92400e' : '#991b1b',
          }}>
            {socketStatus === 'connected' ? '🔌 Socket Live' : socketStatus === 'connecting' ? '⏳ Connecting...' : '❌ Socket Off'}
          </span>
        </div>
      </div>

      {/* How it works */}
      <div style={{ background: '#1a1a2e', color: 'white', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>🗺️ How Live Patrol Tracking Works</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px,1fr))', gap: 8, fontSize: 12 }}>
          {[
            { icon: '📍', t: 'Admin places checkpoint GPS coords on map' },
            { icon: '🚶', t: 'Guard app sends GPS every 3 seconds' },
            { icon: '🗺️', t: 'Admin sees guard moving live on map' },
            { icon: '📡', t: 'Socket.io broadcasts location instantly' },
            { icon: '✅', t: 'GPS + Photo = verified checkpoint scan' },
          ].map((s, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,.08)', borderRadius: 8, padding: 10, textAlign: 'center' }}>
              <div style={{ fontSize: 22 }}>{s.icon}</div>
              <div style={{ color: '#ccc', marginTop: 6 }}>{s.t}</div>
            </div>
          ))}
        </div>
      </div>

      {!activePatrol ? (
        /* ── ROUTE SELECTION ─────────────────────────────────── */
        <div>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 15 }}>Select a Route to Start Patrol</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 16 }}>
            {routes.map(r => (
              <div key={r.id} className="card" style={{ cursor: 'pointer' }} onClick={() => startPatrol(r)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>📍 {r.name}</div>
                    <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>{r.site_name}</div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 6 }}>
                      {r.checkpoint_count} checkpoints · {r.expected_duration_minutes} min
                    </div>
                  </div>
                  <button className="btn btn-primary btn-sm">Start</button>
                </div>
              </div>
            ))}
            {routes.length === 0 && (
              <div className="card" style={{ color: '#9ca3af' }}>No active routes. Create one in Routes first.</div>
            )}
          </div>
        </div>
      ) : (
        /* ── ACTIVE PATROL ───────────────────────────────────── */
        <div className="sim-patrol-grid" style={{ display: 'grid', gridTemplateColumns: '300px 1fr 260px', gap: 16 }}>

          {/* ── LEFT: Checkpoint list ──────────────────────── */}
          <div>
            {/* Status */}
            <div style={{ background: '#1a1a2e', borderRadius: 12, padding: 14, marginBottom: 14, color: 'white' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontWeight: 700 }}>🟢 {selectedRoute.name}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#4361ee' }}>
                  {scannedIds.length}/{checkpoints.length}
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,.1)', borderRadius: 4, height: 8 }}>
                <div style={{
                  width: `${compliance}%`, height: '100%', borderRadius: 4,
                  background: compliance >= 80 ? '#22c55e' : compliance >= 50 ? '#f59e0b' : '#ef233c',
                  transition: 'width .5s',
                }} />
              </div>
              <div style={{ fontSize: 12, color: '#aaa', marginTop: 4, textAlign: 'right' }}>{compliance}%</div>
            </div>

            {/* Simulation controls */}
            <div className="card" style={{ marginBottom: 14 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>🎮 Simulation Controls</div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4 }}>Speed</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[{v:1,l:'🐢 Slow'},{v:2,l:'🚶 Normal'},{v:4,l:'🏃 Fast'}].map(s => (
                    <button key={s.v} onClick={() => setSimSpeed(s.v)}
                      style={{ flex:1, padding:'5px 4px', borderRadius:6, border:'1px solid',
                        borderColor: simSpeed===s.v?'#4361ee':'#e5e7eb',
                        background: simSpeed===s.v?'#eef2ff':'white',
                        color: simSpeed===s.v?'#4361ee':'#374151',
                        fontSize:11, cursor:'pointer', fontWeight: simSpeed===s.v?600:400 }}>
                      {s.l}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={() => { setSimRunning(r => !r); addLog(simRunning ? '⏸ Simulation paused' : '▶ Simulation started', 'info'); }}
                className={simRunning ? 'btn btn-warning' : 'btn btn-primary'}
                style={{ width: '100%', marginBottom: 8 }}>
                {simRunning ? '⏸ Pause Auto-Walk' : '▶ Start Auto-Walk'}
              </button>
              <button onClick={endPatrol} className="btn btn-danger" style={{ width: '100%', fontSize: 13 }}>
                🏁 End Patrol
              </button>
            </div>

            {/* Checkpoints */}
            <div style={{ maxHeight: 380, overflowY: 'auto' }}>
              {checkpoints.map((cp, i) => {
                const done   = scannedIds.includes(cp.id);
                const active = nextCpIdx === i && !done;
                return (
                  <div key={cp.id} onClick={() => !done && !simRunning && (setSelectedCP(cp), setScanResult(null))}
                    style={{
                      border: `2px solid ${done ? '#22c55e' : active ? '#f59e0b' : '#e5e7eb'}`,
                      borderRadius: 10, padding: '10px 12px', marginBottom: 8,
                      background: active ? '#fffbeb' : done ? '#f0fdf4' : 'white',
                      cursor: done || simRunning ? 'default' : 'pointer',
                    }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: 20 }}>{done ? '✅' : active ? '⏳' : '⭕'}</span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>CP{cp.sequence_order}: {cp.name}</div>
                          {active && <div style={{ fontSize: 10, color: '#f59e0b', fontWeight: 600 }}>← Guard walking here</div>}
                          {done && <div style={{ fontSize: 10, color: '#22c55e', fontWeight: 600 }}>Verified ✓</div>}
                        </div>
                      </div>
                      {!done && !simRunning && (
                        <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); setSelectedCP(cp); setScanResult(null); }}>
                          📸
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── CENTER: Map ────────────────────────────────── */}
          <div>
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', height: 520 }}>
              <div style={{ display: 'flex', gap: 6, padding: '6px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                {Object.values(MAP_TILES).map(t => (
                  <button key={t.id} onClick={() => setTile(t.id)}
                    style={{
                      padding: '3px 8px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 11,
                      background: tile === t.id ? '#4361ee' : '#e2e8f0',
                      color: tile === t.id ? 'white' : '#334155', fontWeight: tile === t.id ? 600 : 400,
                    }}>
                    {t.name}
                  </button>
                ))}
              </div>
              <MapContainer center={mapCenter} zoom={17} maxZoom={22} style={{ height: 'calc(100% - 35px)', width: '100%' }}>
                <TileLayer
                  key={tile}
                  url={MAP_TILES[tile]?.url || MAP_TILES.googleHybrid.url}
                  attribution={MAP_TILES[tile]?.attr || '© Google Maps'}
                  maxZoom={22}
                  maxNativeZoom={MAP_TILES[tile]?.maxNativeZoom || 21}
                />

                {/* Guard trail */}
                {trail.length > 1 && (
                  <Polyline positions={trail} color="#4361ee" weight={3} opacity={0.6} dashArray="6 4" />
                )}

                {/* Guard marker */}
                {guardPos && (
                  <>
                    <Marker position={[guardPos.lat, guardPos.lng]} icon={guardIcon(simRunning)}>
                      <Popup>
                        <strong>👮 Guard Simulator</strong><br />
                        Lat: {guardPos.lat.toFixed(6)}<br />
                        Lng: {guardPos.lng.toFixed(6)}<br />
                        Status: {simRunning ? '🟢 Moving' : '🟡 Paused'}
                      </Popup>
                    </Marker>
                    <PanToGuard pos={[guardPos.lat, guardPos.lng]} />
                  </>
                )}

                {/* Checkpoint markers + radius circles */}
                {checkpoints.filter(cp => cp.latitude != null && cp.longitude != null).map((cp, i) => {
                  const done   = scannedIds.includes(cp.id);
                  const active = nextCpIdx === i && !done;
                  return (
                    <React.Fragment key={cp.id}>
                      <Circle
                        center={[cp.latitude, cp.longitude]}
                        radius={cp.radius_meters || 30}
                        color={done ? '#22c55e' : active ? '#f59e0b' : '#9ca3af'}
                        fillOpacity={0.15}
                        weight={2}
                      />
                      <Marker position={[cp.latitude, cp.longitude]} icon={cpIcon(done, active)}>
                        <Popup>
                          <strong>CP{cp.sequence_order}: {cp.name}</strong><br />
                          Radius: {cp.radius_meters || 30}m<br />
                          {done ? '✅ Scanned' : active ? '⏳ Guard en route' : '⭕ Pending'}
                        </Popup>
                      </Marker>
                    </React.Fragment>
                  );
                })}
              </MapContainer>
            </div>

            {/* Map legend */}
            <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: '#666', padding: '0 4px' }}>
              <span>👮 Guard position (live)</span>
              <span style={{ color: '#4361ee' }}>━ Trail</span>
              <span style={{ color: '#22c55e' }}>● Scanned CP</span>
              <span style={{ color: '#f59e0b' }}>● Next CP</span>
              <span style={{ color: '#9ca3af' }}>● Pending CP</span>
            </div>
          </div>

          {/* ── RIGHT: Scan panel + log ────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Manual scan panel */}
            {!simRunning && selectedCP && !scannedIds.includes(selectedCP.id) && (
              <div className="card">
                {!scanResult ? (
                  <>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                      {selectedCP.scan_type === 'gps' ? '📡 Verify GPS' : '📸 Scan'} CP{selectedCP.sequence_order}
                    </div>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>{selectedCP.name}</div>

                    {selectedCP.scan_type === 'gps' ? (
                      /* GPS-only: just show verify button */
                      <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: 16, textAlign: 'center', marginBottom: 12 }}>
                        <div style={{ fontSize: 28 }}>📡</div>
                        <div style={{ color: '#16a34a', fontSize: 13, fontWeight: 600, marginTop: 6 }}>GPS Verify Only</div>
                        <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>No photo required for this checkpoint</div>
                      </div>
                    ) : (
                      /* Photo type */
                      photo ? (
                        <div>
                          <img src={URL.createObjectURL(photo)} alt="preview"
                            style={{ width: '100%', borderRadius: 8, maxHeight: 140, objectFit: 'cover', marginBottom: 8 }} />
                          <button className="btn btn-sm" style={{ width: '100%', background: '#f3f4f6', marginBottom: 8 }}
                            onClick={() => setPhoto(null)}>Retake</button>
                        </div>
                      ) : (
                        <div onClick={() => fileRef.current.click()}
                          style={{ border: '2px dashed #4361ee', borderRadius: 8, padding: 20, textAlign: 'center', cursor: 'pointer', marginBottom: 12 }}>
                          <div style={{ fontSize: 28 }}>📷</div>
                          <div style={{ color: '#4361ee', fontSize: 13, fontWeight: 600 }}>Add photo</div>
                          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={e => setPhoto(e.target.files[0])} />
                        </div>
                      )
                    )}

                    <textarea className="form-input" rows={2} placeholder="Notes (optional)"
                      value={notes} onChange={e => setNotes(e.target.value)}
                      style={{ resize: 'none', fontSize: 12, marginBottom: 10 }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-sm" style={{ background: '#f3f4f6' }} onClick={() => setSelectedCP(null)}>✕</button>
                      <button className="btn btn-primary btn-sm" style={{ flex: 1 }}
                        onClick={submitManualScan}
                        disabled={(selectedCP.scan_type !== 'gps' && !photo) || submitting}>
                        {submitting ? '...' : selectedCP.scan_type === 'gps' ? '📡 Verify' : '✅ Submit'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '8px 0' }}>
                    <div style={{ fontSize: 36 }}>{scanResult.status === 'verified' ? '✅' : '❌'}</div>
                    <div style={{ fontWeight: 700, marginTop: 8 }}>
                      {scanResult.status === 'verified' ? 'Verified!' : 'Failed'}
                    </div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{scanResult.message}</div>
                    <button className="btn btn-sm btn-primary" style={{ marginTop: 10, width: '100%' }}
                      onClick={() => { setSelectedCP(null); setScanResult(null); }}>
                      Back
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Simulation activity log */}
            <div className="card" style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>📋 Activity Log</div>
              <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {simLog.length === 0 && (
                  <div style={{ color: '#9ca3af', fontSize: 12, textAlign: 'center', padding: 16 }}>
                    Start simulation to see activity
                  </div>
                )}
                {simLog.map((log, i) => (
                  <div key={i} style={{ fontSize: 12, display: 'flex', gap: 8, padding: '4px 6px', borderRadius: 6, background: '#f9fafb' }}>
                    <span style={{ color: '#9ca3af', flexShrink: 0 }}>{log.time}</span>
                    <span style={{ color: log.color }}>{log.msg}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tip card */}
            <div className="card" style={{ background: '#eef2ff', border: '1px solid #c7d2fe' }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#4361ee', marginBottom: 8 }}>💡 How to test</div>
              <div style={{ fontSize: 11, color: '#4338ca', lineHeight: 1.6 }}>
                1. Click ▶ Start Auto-Walk<br />
                2. Open Live Map in another tab<br />
                3. Watch guard move in real time<br />
                4. Green circle = guard within CP range<br />
                5. Auto-scan triggers when arrived
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
